import { createCasino, localSigner, constants, catalogWordHash, verifyRegistered, predictWords } from "@freeroll/sdk/v14";
import { createPublicClient, http } from "viem";
import { generatePrivateKey, privateKeyToAccount } from "viem/accounts";
import { buildOverUnder, OVER_UNDER, OVER_UNDER_SPRINT, RANK_NAMES } from "./game.mjs";
import { hyperEvmTestnet, throttledFetch } from "./chain.mjs";
import { play, isMuted, setMuted } from "./sound.js";
import DEPLOYMENT from "../deployment.json";

const VAULT = DEPLOYMENT.vault;
const HOUSE_URL = "https://variance-vault-keeper.fly.dev/v14";
const RPC = "https://rpc.hyperliquid-testnet.xyz/evm";
const FAUCET = "https://testnet.variancevault.xyz/api/faucet";
const SITE = "https://testnet.variancevault.xyz";
const MIN = constants.MIN_STAKE_MICRO;
const VAULT_ABI_MIN = [
  { type: "function", name: "gameNodeWords", stateMutability: "view",
    inputs: [{ type: "uint256" }, { type: "uint256" }, { type: "uint256" }],
    outputs: [{ type: "bytes32" }] },
  { type: "function", name: "gameMeta", stateMutability: "view",
    inputs: [{ type: "uint256" }, { type: "uint256" }],
    outputs: [{ type: "uint32" }, { type: "uint32" }, { type: "uint64" }, { type: "uint64" },
              { type: "uint32" }, { type: "bool" }] },
];

// The public RPC rate-limits a single browser easily. Three things keep us under it:
//   multicall3 (declared on the chain)  — collapses the vault's many reads into one eth_call
//   throttledFetch                      — serializes with a floor interval and backs off on 429
// Without multicall3, the engine's first-bet catalog verify fires ~4800 separate eth_calls and
// 429s the player instantly.
//
// Do NOT add viem's `batch: true` here. It packs several JSON-RPC calls into one HTTP body, and
// the catalog verify's chunks are already ~85KB aggregate3 calls each — three of those in one
// body is rejected by the RPC. The SDK surfaces that read failure as "catalog mismatch: local
// game does not match set N index 0", which reads as game drift rather than a network error.
// Measured: batch on => 2289/2289 word reads fail; batch off => 0 fail.
const publicClient = createPublicClient({
  chain: hyperEvmTestnet,
  transport: http(RPC, {
    retryCount: 4, retryDelay: 1000, timeout: 60_000,
    fetchFn: throttledFetch({ minIntervalMs: 80, maxRetries: 4 }),
  }),
});

// Per-player burner wallet — no backend, no custody. (Testnet money; the key lives in this browser.)
const pk = localStorage.getItem("ou.pk") ??
  (() => { const k = generatePrivateKey(); localStorage.setItem("ou.pk", k); return k; })();
const account = privateKeyToAccount(pk);

// The local catalog is rebuilt from source and must be index-aligned with the on-chain set —
// deployment.json fixes that order, and the engine deep-verifies the words on first play.
const CONFIGS = { "over-under": OVER_UNDER, "over-under-sprint": OVER_UNDER_SPRINT };
// Games this frontend offers. The engine requires local index == on-chain index, so we take the
// longest PREFIX of the on-chain catalog that is listed — never an arbitrary subset, which would
// silently misalign every game after the gap. Shipping only "over-under" (index 0) also halves
// the first-bet catalog verify.
const LISTED = ["over-under"];
const ORDERED = [...DEPLOYMENT.games].sort((a, b) => a.gameIndex - b.gameIndex);
const prefix = [];
for (const g of ORDERED) { if (!LISTED.includes(g.name)) break; prefix.push(g); }
if (!prefix.length) throw new Error("no listed game at on-chain index 0");
const CATALOG = prefix.map((g, i) => {
  if (g.gameIndex !== i) throw new Error(`catalog misalignment: ${g.name} at index ${g.gameIndex}, expected ${i}`);
  return { ...buildOverUnder(CONFIGS[g.name]), meta: g };
});
const compiled = CATALOG.map((c) => c.compiled);
const byName = new Map(CATALOG.map((c) => [c.compiled.name, c]));

const casino = createCasino({
  chainId: 998, publicClient, vault: VAULT, houseUrl: HOUSE_URL,
  player: account.address,
  signer: () => localSigner(pk, publicClient),
  gameSetId: BigInt(DEPLOYMENT.gameSetId),
  games: compiled,
});

// ---------- helpers ----------
const $ = (id) => document.getElementById(id);
const usd = (micro) => `$${(Number(micro) / 1e6).toFixed(2)}`;
const mult = (ppm) => `${(ppm / 1e6).toFixed(2)}×`;
const parseKey = (k) => { try { return JSON.parse(k); } catch { return null; } };

/** The COMMITTED odds of a call, read off the registered tree (not the ideal fraction) —
 *  13 ranks don't divide 10000 bps, so this is the number money actually settles against. */
function committedOdds(game, k, r, side) {
  const idx = game.indexByKey.get(JSON.stringify({ k, r, side, t: "draw" }));
  if (idx == null) return null;
  const node = game.nodes[idx], labels = game.info[idx].outcomeLabels ?? [];
  let win = 0, push = 0, bust = 0;
  node.children.forEach((ch, i) => {
    const l = labels[i];
    if (l === "push") { push += ch.widthBps; return; }
    if (l === "bust") { bust += ch.widthBps; return; }          // superseded merged trees
    const rank = RANK_NAMES.indexOf(l);                          // every loser is its own rank now
    if ((side === "over" ? rank > r : rank < r)) win += ch.widthBps; else bust += ch.widthBps;
  });
  return { win: win / 100, push: push / 100, bust: bust / 100 };
}

let state = { game: CATALOG[0].compiled.name, round: null, busy: false, stakeMicro: 100_000n, view: null, lastRung: 0, spendable: null };
const GAME = CATALOG[0];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

function setStatus(msg, kind = "") { const el = $("status"); el.textContent = msg; el.className = `status ${kind}`; }

async function refreshBalance() {
  // Resolve the two reads independently and KEEP the last known value on a transient failure.
  // Failing them together blanked Balance to "—" while Spendable still showed a figure, which
  // looks like the wallet broke rather than one read hiccuping.
  const [bal, spend] = await Promise.all([
    casino.balanceMicro().catch(() => null),
    casino.spendableMicro().catch(() => null),
  ]);
  if (bal !== null) $("balance").textContent = usd(bal);
  if (spend !== null) { $("spendable").textContent = usd(spend); state.spendable = spend; }
  $("betBtn").disabled = (state.spendable ?? 0n) < MIN || state.busy || !!state.round;
}

async function refreshCeiling() {
  try {
    const max = await casino.game(state.game).maxStakeMicro();
    $("ceiling").textContent = usd(max);
    // Display only — never write the ceiling back into the stake. Doing that at boot (when the
    // wallet is empty and max is $0.00) silently pinned the stake to zero, and it never recovered
    // once the faucet landed. planStake() is the thing that curbs a bet, at bet time.
    $("stake").max = max > 0n ? String(Number(max) / 1e6) : "";
  } catch { $("ceiling").textContent = "—"; }
}

// ---------- rendering ----------
function cardHtml(label, cls = "") {
  return `<div class="card ${cls}"><span class="rank">${label}</span></div>`;
}
const rankCard = (r, cls) => cardHtml(RANK_NAMES[r], cls);

/** Face-down card, dealt in. `flip()` turns it over onto `label`. */
function dealFaceDown(container) {
  const wrap = document.createElement("div");
  wrap.className = "flipwrap dealing";
  wrap.innerHTML = `<div class="flipper"><div class="face back"></div>` +
                   `<div class="face front"><span class="rank"></span></div></div>`;
  container.appendChild(wrap);
  return {
    async flip(label, cls) {
      const flipper = wrap.querySelector(".flipper");
      wrap.querySelector(".front .rank").textContent = label;
      if (cls) wrap.querySelector(".front").classList.add(cls);
      await sleep(reduceMotion ? 0 : 90);
      play("flip");
      flipper.classList.add("flipped");
      await sleep(reduceMotion ? 0 : 520);
    },
  };
}

function renderLadder(name, currentRung) {
  const { ladder } = byName.get(name);
  const top = ladder.length;                       // the top rung is a forced payout, not a perch
  $("ladder").innerHTML = ladder.map((m, i) => {
    const rung = i + 1;
    const cls = rung === currentRung ? "rung now" : rung < currentRung ? "rung done" : "rung";
    return `<div class="${cls}${rung === top ? " top" : ""}" ${rung === top
      ? `title="Top of the ladder — a correct call here pays out automatically; the round ends."` : ""}>` +
      `<b>${rung}${rung === top ? " ⚑" : ""}</b><span>${mult(m)}</span></div>`;
  }).join("");
}

function renderIdle() {
  const g = byName.get(state.game);
  $("table").innerHTML = `<div class="idle">
      <div class="deck">${cardHtml("?")}</div>
      <p>Place a bet to deal your first card.</p></div>`;
  $("actions").innerHTML = "";
  renderLadder(state.game, 0);
  $("roundinfo").textContent = `Top rung pays ${mult(g.ladder[g.ladder.length - 1])} · house edge ${((1e6 - g.compiled.rootValuePpm) / 1e4).toFixed(2)}%`;
}

function renderRound(view) {
  state.view = view;
  const st = parseKey(view.stateKey);
  const g = byName.get(state.game);

  if (view.done) {
    const out = view.outcome;
    const won = out.payoutMicro > 0n;
    // The last card in the transcript is the one that ended it.
    const reveals = view.transcript.filter((s) => s.kind === "reveal");
    const last = reveals[reveals.length - 1];
    // Four different ways a round ends. Calling all three winning ones "banked" made a
    // top-of-ladder auto-cash look like the app had clicked Bank on the player's behalf.
    const steps = view.transcript;
    const tail = steps[steps.length - 1];
    const banked = tail?.kind === "action" && tail.actionName === "bank";
    const pushed = last?.label === "push";
    const toppedOut = won && !banked && !pushed;
    const rungsCleared = state.lastRung ?? 0;
    // "push"/"bust" are labels, not ranks — the card you were STANDING on is the reveal before it.
    const rankOf = (l) => RANK_NAMES.indexOf(l);
    const standing = rankOf(last?.label) > 0 ? rankOf(last.label) : rankOf(reveals[reveals.length - 2]?.label);
    const beaten = rankOf(reveals[reveals.length - 2]?.label);      // the card we were standing on
    const endCards = pushed && standing > 0
      ? rankCard(standing, "push") + rankCard(standing, "push")      // the matching pair
      : !won && standing > 0 && beaten > 0
        ? rankCard(beaten) + rankCard(standing, "miss")              // yours, then the one that beat it
        : standing > 0 ? rankCard(standing, won ? "hit" : "miss") : "";
    const headline = !won ? "Bust"
      : banked ? `Banked ${usd(out.payoutMicro)}`
      : pushed ? `Push — ${usd(out.payoutMicro)}`
      : `Top of the ladder — ${usd(out.payoutMicro)}`;
    const detail = !won
      ? `${standing > 0 && beaten > 0 ? `${RANK_NAMES[standing]} against your ${RANK_NAMES[beaten]} — ` : ""}` +
        `lost ${usd(out.stakedMicro)} ${rungsCleared > 0 ? `on rung ${rungsCleared}` : "on your first call"}`
      : banked ? `${(Number(out.payoutMicro) / Number(out.stakedMicro)).toFixed(2)}× on ${usd(out.stakedMicro)}`
      : pushed ? `Two ${standing > 0 ? RANK_NAMES[standing] + "s" : "matching cards"} — neither over nor under. A push pays the rung you were standing on.`
      : `You cleared every rung. The top rung cashes out automatically — the round ends there.`;
    $("table").innerHTML = `<div class="result ${pushed ? "push" : won ? "win" : "lose"}">
        <div class="cards">${endCards}</div>
        <h2>${headline}</h2>
        <p>${detail}</p>
      </div>`;
    $("actions").innerHTML = "";
    $("roundinfo").textContent =
      `Top rung pays ${mult(g.ladder[g.ladder.length - 1])} · house edge ${((1e6 - g.compiled.rootValuePpm) / 1e4).toFixed(2)}%`;
    setStatus(toppedOut ? "Ladder maxed — the top rung pays out automatically."
      : banked ? "Banked." : pushed ? "Push — round ended at your current rung." : "Busted.",
      won ? "good" : "bad");
    return;
  }

  if (!st || st.t !== "card") { $("actions").innerHTML = ""; return; }   // mid-reveal
  const rung = st.k, r = st.r;
  state.lastRung = rung;
  renderLadder(state.game, rung);

  // "holding" must be what Bank actually pays. cashValueMicro is the contract's abandon mark,
  // which is HIGHER at a favourable card because it prices in playing the round out optimally —
  // showing it next to a smaller "Bank $X" button reads as the button short-changing you.
  const bankAction = view.actions.find((a) => a.name === "bank");
  const over = committedOdds(g.compiled, rung, r, "over");
  const under = committedOdds(g.compiled, rung, r, "under");
  $("table").innerHTML = `<div class="live">
      <div class="cardstack">${rankCard(r)}</div>
      <div class="meta">
        <div class="rung-label">${rung
          ? `Rung ${rung} of ${g.ladder.length}${bankAction ? ` · holding ${usd(bankAction.valueMicro)}` : ""}`
          : "First call · not on the ladder yet"}</div>
        <div class="odds"><span>over ${over.win.toFixed(2)}%</span><span>push ${over.push.toFixed(2)}%</span><span>under ${under.win.toFixed(2)}%</span></div>
      </div></div>`;

  const topRung = g.ladder.length;
  $("roundinfo").innerHTML = rung === topRung - 1
    ? `<span class="hint">Last call: get this right and the ladder maxes at ${mult(g.ladder[topRung - 1])} and pays out automatically.</span>`
    : `Top rung pays ${mult(g.ladder[topRung - 1])} · house edge ${((1e6 - g.compiled.rootValuePpm) / 1e4).toFixed(2)}%`;

  $("actions").innerHTML = view.actions.map((a) => {
    const label = a.name === "over" ? `Over <small>${over.win.toFixed(1)}%</small>`
      : a.name === "under" ? `Under <small>${under.win.toFixed(1)}%</small>`
      : `Bank ${usd(a.valueMicro)}`;
    return `<button class="act ${a.name}" data-act="${a.name}">${label}</button>`;
  }).join("");
  for (const b of $("actions").querySelectorAll("button")) b.onclick = () => act(b.dataset.act);
}

// ---------- actions ----------
async function bet() {
  if (state.busy || state.round) return;
  state.busy = true; $("betBtn").disabled = true;
  setStatus("Opening channel & dealing…");
  try {
    const handle = casino.game(state.game);
    // planStake is the one authority: it curbs the intent to what the pool + bankroll can seat,
    // so the advertised bet and the real wager can never disagree.
    const plan = await handle.planStake({ requestedTotalMicro: state.stakeMicro });
    if (!plan.canBet) throw new Error(plan.belowMinBalance
      ? "Not enough spendable balance for a $0.10 minimum — get test funds."
      : `That stake can't be seated right now (limited by ${plan.cappedBy}).`);
    play("deal");
    const round = await handle.start(plan.perShotMicro);
    state.round = round;
    const curbed = plan.totalMicro < state.stakeMicro;
    if (curbed) { state.stakeMicro = plan.totalMicro; $("stake").value = (Number(plan.totalMicro) / 1e6).toFixed(2); }
    setStatus(curbed ? `Stake curbed to ${usd(plan.totalMicro)} by the ${plan.cappedBy} cap. Call the next card.`
                     : "Call the next card.");
    const opening = await round.view();
    const openSt = parseKey(opening.stateKey);
    if (openSt?.t === "card") {                       // flip the opening card in, same as any reveal
      $("table").innerHTML = `<div class="live"><div class="cardstack"></div></div>`;
      $("actions").innerHTML = "";
      await dealFaceDown($("table").querySelector(".cardstack")).flip(RANK_NAMES[openSt.r]);
      await sleep(reduceMotion ? 0 : 260);
    }
    renderRound(opening);
  } catch (e) {
    setStatus(e.message ?? String(e), "bad");
  } finally { state.busy = false; refreshBalance(); refreshCeiling(); }
}

async function act(name) {
  if (state.busy || !state.round) return;
  state.busy = true;
  for (const b of $("actions").querySelectorAll("button")) b.disabled = true;

  const prev = parseKey(state.view?.stateKey ?? "null");     // the card we are standing on
  const standing = prev?.t === "card" ? prev.r : null;

  if (name === "bank") {
    setStatus("Banking…");
    play("bank");
    try {
      const view = await state.round.act(name);
      state.round = null;
      renderRound(view);
    } catch (e) { setStatus(e.message ?? String(e), "bad"); }
    finally { state.busy = false; refreshBalance(); refreshCeiling(); }
    return;
  }

  // Deal a face-down card next to the current one, then turn it over on the real result.
  setStatus(`Calling ${name}…`);
  play("deal");
  const slot = $("table").querySelector(".cardstack") ?? $("table");
  const card = dealFaceDown(slot);

  try {
    const view = await state.round.act(name);
    const reveals = view.transcript.filter((x) => x.kind === "reveal");
    const label = reveals[reveals.length - 1]?.label;
    const rank = RANK_NAMES.indexOf(label);

    const lost = view.done && view.outcome && view.outcome.payoutMicro === 0n;
    if (label === "push") {
      // A tie: the drawn card MATCHES the one we were standing on. Show that, loudly.
      await card.flip(RANK_NAMES[standing] ?? "=", "push");
      play("push");
      $("tieline")?.remove();
      const note = document.createElement("div");
      note.id = "tieline";
      note.className = "tieline";
      note.innerHTML = `<b>TIE — push.</b> The next card matched your ${RANK_NAMES[standing]}. ` +
        `Neither over nor under: the round ends here and pays the rung you were on.`;
      $("table").appendChild(note);
      await sleep(reduceMotion ? 0 : 1300);
    } else if (rank > 0) {
      await card.flip(label, lost ? "miss" : "hit");
      play(lost ? "bust" : view.done ? "top" : "climb");
      await sleep(reduceMotion ? 0 : 620);
    } else {
      await card.flip("✕", "miss");        // only reachable on a superseded merged tree
      play("bust");
      await sleep(reduceMotion ? 0 : 620);
    }

    if (view.done) state.round = null;
    else setStatus("Call the next card.");
    renderRound(view);
  } catch (e) { setStatus(e.message ?? String(e), "bad"); }
  finally { state.busy = false; refreshBalance(); if (!state.round) refreshCeiling(); }
}

async function cashOutSession() {
  if (state.busy) return;
  state.busy = true; $("leaveBtn").disabled = true; $("betBtn").disabled = true;
  setStatus(state.round ? "Settling — an open round settles at its current cash mark…" : "Settling on-chain…");
  try { await casino.leave(); setStatus("Session settled. Balance is back in the vault.", "good"); }
  catch (e) { setStatus(e.message ?? String(e), "bad"); }
  finally {
    state.busy = false; state.round = null; $("leaveBtn").disabled = false;
    renderIdle(); refreshBalance(); refreshCeiling();
  }
}

async function faucet() {
  setStatus("Requesting testnet funds…");
  try {
    const res = await fetch(FAUCET, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ address: account.address }),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) { setStatus(j.error ?? "Faucet declined.", "bad"); }
    else {
      // The faucet returns before the deposit is visible on chain — poll instead of showing $0.00.
      setStatus("Funded — waiting for the deposit to land…");
      for (let i = 0; i < 12; i++) {
        await refreshBalance();
        if ((await casino.spendableMicro().catch(() => 0n)) >= MIN) break;
        await sleep(2000);
      }
      await refreshCeiling();                       // the ceiling is balance-bound too
      setStatus("Funded — $10 test USDC deposited.", "good");
    }
  } catch {
    setStatus(`Faucet blocked by CORS from this origin. Fund manually: POST {"address":"${account.address}"} to ${FAUCET}`, "bad");
  }
  refreshBalance(); refreshCeiling();
}

// The house force-closes a round left open too long. The SDK leaves the cadence to us, so
// ping while a decision is pending and the tab is visible — otherwise a player who steps away
// mid-ladder comes back to a round that settled without them.
setInterval(() => {
  if (state.round && !state.busy && document.visibilityState === "visible") casino.keepalive().catch(() => {});
}, 20_000);

// ---------- boot ----------
$("addr").textContent = `${account.address.slice(0, 6)}…${account.address.slice(-4)}`;
$("addr").title = account.address;
$("addr").onclick = () => navigator.clipboard?.writeText(account.address);
$("faucetBtn").onclick = faucet;
const paintMute = () => { $("muteBtn").textContent = isMuted() ? "🔇" : "🔊"; };
$("muteBtn").onclick = () => { setMuted(!isMuted()); paintMute(); if (!isMuted()) play("flip"); };
paintMute();
$("leaveBtn").onclick = cashOutSession;
$("betBtn").onclick = bet;
$("stake").oninput = (e) => {
  const v = Math.max(0, Number(e.target.value) || 0);
  state.stakeMicro = BigInt(Math.round(v * 1e6));
};
// Rules live in a modal now — the sidebar was mostly text.
const modal = $("modal");
const openModal = () => { modal.hidden = false; $("modalClose").focus(); };
const closeModal = () => { modal.hidden = true; };
$("rulesBtn").onclick = openModal;
$("modalClose").onclick = closeModal;
modal.onclick = (e) => { if (e.target === modal) closeModal(); };
document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !modal.hidden) closeModal(); });

$("chainline").textContent =
  `set ${DEPLOYMENT.gameSetId} · vault ${VAULT} · HyperEVM testnet (998)`;
$("cataloglink").href =
  `${SITE}/games/custom/${DEPLOYMENT.gameSetId}/${CATALOG[0].meta.gameIndex}`;

// Small debug surface: lets a test (or a curious player) confirm the tree the page built
// matches the tree the chain holds, without a devtools session.
window.overUnder = {
  gameSetId: DEPLOYMENT.gameSetId, vault: VAULT,
  catalog: CATALOG.map((c) => ({
    name: c.compiled.name, gameIndex: c.meta.gameIndex,
    nodes: c.compiled.meta.nodeCount, childEntries: c.compiled.meta.childEntryCount,
    rootValuePpm: c.compiled.rootValuePpm, rungs: c.ladder.length,
  })),
  wordHash: catalogWordHash(compiled),
  async verify() {
    const out = [];
    for (const c of CATALOG) {
      const idx = BigInt(c.meta.gameIndex);
      let ok, err = null;
      try { ok = await verifyRegistered(publicClient, VAULT, BigInt(DEPLOYMENT.gameSetId), idx, c.compiled); }
      catch (e) { ok = "threw"; err = String(e.message).split("\n")[0]; }
      // Read the first words directly and diff them against the prediction.
      const want = predictWords(c.compiled.nodes, c.compiled.freeVals, c.compiled.depths);
      let got = null, readErr = null;
      try {
        got = await publicClient.readContract({ address: VAULT, abi: VAULT_ABI_MIN,
          functionName: "gameNodeWords", args: [BigInt(DEPLOYMENT.gameSetId), idx, 0n] });
      } catch (e) { readErr = String(e.message).split("\n")[0]; }
      out.push({ name: c.compiled.name, gameIndex: c.meta.gameIndex, ok, err,
                 wantWord0: want.nodeWords[0], gotWord0: got, readErr,
                 wantNodes: c.compiled.meta.nodeCount, wantChildEntries: c.compiled.meta.childEntryCount });
    }
    return out;
  },
};

renderIdle();
await refreshBalance();
await refreshCeiling();
// A new visitor's wallet is empty and Deal is disabled — say why, rather than showing a dead button.
if ((await casino.spendableMicro().catch(() => 0n)) < MIN) {
  setStatus("New here? Hit “Get test funds” for $10 of testnet USDC — this is a burner wallet in your browser.");
}
// A reload mid-round resumes the same channel rather than stranding the stake.
try {
  const r = await casino.resume();
  if (r && r.game === state.game) { state.round = r; renderRound(await r.view()); setStatus("Resumed your round."); }
} catch {}
